import Api from '@/api';
import router from '@/router';

import { apiAction, apiFetch } from './apiState';

const state = {
  profile: {
    iam: {},
  },
  allProfiles: [],
  selectedProfile: {},
  admin: false,
  profilePhrases: [],
  companyPhrases: [],
  phraseFilter: '',
  loginCounter: 0,
};

const actions = {
  getProfile: apiFetch(async ({commit}) => {
    const response = await Api.getProfile();

    const profile = response.data;
    commit('PROFILE_LOADED', profile);
    commit('PROFILE_PHRASES_LOADED', profile.phrases || []);
    commit('COMPANY_PHRASES_LOADED', profile.companyPhrases || []);

    return profile;
  }),
  getAllProfiles: apiFetch(async ({commit}) => {
    const response = await Api.getListOfProfiles();

    const profiles = response.data;
    commit('ALL_PROFILES_LOADED', profiles);
    return profiles;
  }),
  saveProfile: apiAction(async ({commit, dispatch, state}, profile) => {
    const userId = state.selectedProfile.userId;

    const editedProfile = {
      firstName: profile.firstName,
      lastName: profile.lastName,
      role: profile.role,
      roleEN: profile.roleEN || 'Vet',
      roleSV: profile.roleSV || 'Veterinär',
      email: profile.email,
      pnumber: profile.pnumber,
    };

    if (userId === state.profile.userId) {
      const response = await Api.saveProfile(editedProfile);

      const newProfile = response.data;
      commit('SELECTED_PROFILE_LOADED', newProfile);
      commit('PROFILE_LOADED', newProfile);
      dispatch('getChats');

      return newProfile;
    }

    const response = await Api.saveSomeonesProfile({
      ...editedProfile,

      company: profile.company,
    }, userId);

    const newProfile = response.data;
    commit('SELECTED_PROFILE_LOADED', newProfile);
    dispatch('getAllProfiles');
    return newProfile;
  }),
  createProfile: apiAction(async ({commit, dispatch}, profile) => {
    const response = await Api.createNewProfile({
      firstName: profile.firstName,
      lastName: profile.lastName,
      role: profile.role,
      roleEN: profile.roleEN || 'Vet',
      roleSV: profile.roleSV || 'Veterinär',
      company: profile.company,
      email: profile.email,
      pnumber: profile.pnumber,
    }, profile.email);

    const newProfile = response.data;
    commit('SELECTED_PROFILE_LOADED', newProfile);
    dispatch('getAllProfiles');
    return newProfile;
  }),
  saveProfileImage: apiAction(async ({commit, state}, image) => {
    const userId = state.selectedProfile.userId;

    if (userId === state.profile.userId) {
      const response = await Api.saveProfileImage(image);
      const profile = response.data;
      commit('PROFILE_LOADED', profile);
      return profile;
    }

    const response = await Api.saveSomeonesProfileImage(image, userId);
    const profile = response.data;
    commit('SELECTED_PROFILE_LOADED', profile);
    return profile;
  }),
  deactivateProfile: apiAction(async ({commit}, userId) => {
    const response = await Api.deactivateProfile(userId);
    const profile = response.data;
    commit('SELECTED_PROFILE_LOADED', profile);
    commit('PROFILE_UPDATED', profile);
    return profile;
  }),
  activateProfile: apiAction(async ({commit}, userId) => {
    const response = await Api.activateProfile(userId);
    const profile = response.data;
    commit('SELECTED_PROFILE_LOADED', profile);
    commit('PROFILE_UPDATED', profile);
    return profile;
  }),
  giveSuperUserRights: apiAction(async ({commit}, userId) => {
    const response = await Api.giveSuperUserRights(userId);
    const profile = response.data;
    commit('SELECTED_PROFILE_LOADED', profile);
    commit('PROFILE_UPDATED', profile);
    return profile;
  }),
  removeSuperUserRights: apiAction(async ({commit}, userId) => {
    const response = await Api.removeSuperUserRights(userId);
    const profile = response.data;
    commit('SELECTED_PROFILE_LOADED', profile);
    commit('PROFILE_UPDATED', profile);
    return profile;
  }),
  savePhrase: apiAction(async ({commit, dispatch, state}, phrase) => {
    const company = state.profile.admin && phrase.hashtag.startsWith('#');

    const p = {
      hashtag: phrase.hashtag.replace(/^#+/, ''),
      phrase: phrase.phrase,
    };

    if (company) {
      await Api.saveCompanyPhrase(p);
      await dispatch('getProfile');
    } else {
      const userId = state.profile.userId;

      const response = await Api.savePhrase(userId, p);

      const phrases = response.data.phrases;
      commit('PROFILE_PHRASES_LOADED', phrases);
    }
  }),
  deletePhrase: apiAction(async ({commit, dispatch, state}, phrase) => {
    const hashtag = phrase.hashtag.replace(/^#+/, '');

    if (phrase.company) {
      await Api.deleteCompanyPhrase(hashtag);
      await dispatch('getProfile');
    } else {
      const response = await Api.deletePhrase(state.profile.userId, hashtag);
      const phrases = response.data.phrases;
      commit('PROFILE_PHRASES_LOADED', phrases);
    }
  }),
  setSelectedProfile: apiAction(async ({commit}, profileId) => {
    const response = await Api.findProfile(profileId);

    const profile = response.data;
    commit('SELECTED_PROFILE_LOADED', profile);
    router.push(`/admin/${profileId}`);
    return profile;
  }),
  setNewProfile({commit}) {
    const profile = {
      userId: null,
      company: '',
      email: '',
      firstName: '',
      image: '',
      lastName: '',
      name: '',
      role: '',
      roleEN: '',
      roleSV: '',
      pnumber: '',
    };
    commit('SELECTED_PROFILE_LOADED', profile);
  },
  changeToAdmin({commit, dispatch}) {
    // TODO: fix improper mutation of state
    state.admin = true;
    commit('PROFILE_LOADED', state.profile);
    dispatch('getChats');
  },
  async changeToNormalUser({commit, dispatch}) {
    // TODO: fix improper mutation of state
    state.admin = false;
    commit('PROFILE_LOADED', state.profile);
    dispatch('getChats');
  },
};

const mutations = {
  PROFILE_LOADED: (state, profile) => {
    state.profile = {
      ...profile,

      iam: profile.iam || {},

      admin: state.admin,
    };
  },
  PROFILE_PHRASES_LOADED: (state, phrases) => {
    const newPhrases = Object.entries(phrases).map((([hashtag, phrase]) => ({
      hashtag,
      phrase,
    })));
    newPhrases.sort(sortPhrases);
    state.profilePhrases = newPhrases;
  },
  COMPANY_PHRASES_LOADED: (state, phrases) => {
    const newPhrases = Object.entries(phrases).map((([hashtag, phrase]) => ({
      company: true,
      hashtag,
      phrase,
    })));
    newPhrases.sort(sortPhrases);
    state.companyPhrases = newPhrases;
  },
  ALL_PROFILES_LOADED: (state, profiles) => {
    state.allProfiles = profiles;
    if(profiles) {
      state.allProfiles.sort(sortProfiles);
    }
  },
  PROFILE_UPDATED: (state, profile) => {
    let allProfiles = [...state.allProfiles];

    const index = allProfiles.findIndex(({userId}) => userId === profile.userId);
    if (index >= 0) {
      allProfiles[index] = profile;
    } else {
      allProfiles.push(profile);
    }

    state.allProfiles = allProfiles;
  },
  SELECTED_PROFILE_LOADED: (state, profile) => {
    state.selectedProfile = profile;
  },
  SET_PHRASE_FILTER: (state, filter) => {
    state.phraseFilter = filter;
  },
  LOGGED_IN: (state) => {
    state.loginCounter = state.loginCounter + 1;
  },
  SET_LOGGED_IN_USERS: (state, userIds) => {
    state.allProfiles = (state.allProfiles || []).map(user => {
      return {
        ...user,

        loggedIn: userIds.includes(user.userId),
      };
    });
    state.allProfiles.sort(sortProfiles);
  },
  REMOVE_LOGGED_IN_USER (state, userId) {
    const index = state.allProfiles.findIndex(profile => profile.userId === userId);
    if (index >= 0) {
      state.allProfiles[index] = {
        ...state.allProfiles[index],

        loggedIn: false,
      };
    }
    state.allProfiles.sort(sortProfiles);
  },
};
const getters = {
  profile: state => state.profile,
  allProfiles: state => state.allProfiles,
  activeProfiles: state => state.allProfiles.filter(p => p.active),
  deactivatedProfiles: state => state.allProfiles.filter(p => !p.active),
  selectedProfile: state => state.selectedProfile,
  profilePhrases: state => state.profilePhrases,
  companyPhrases: state => state.companyPhrases,
  allPhrases: state => [ ...state.profilePhrases, ...state.companyPhrases ],
  filteredPhrases: (state, getters) => {
    const filter = state.phraseFilter.replace(/^#/, '');
    const phrases = state.phraseFilter.startsWith('#') ? state.companyPhrases : getters.allPhrases;

    return phrases.filter(phrase => phrase.hashtag.includes(filter));
  },
  loginCounter: state => state.loginCounter,
};

function sortProfiles(a, b) {
  return b.loggedIn - a.loggedIn ||
         (a.lastName || '').localeCompare((b.lastName || ''), 'fi', {sensitivity: 'accent'}) ||
         (a.firstName || '').localeCompare((b.firstName || ''), 'fi', {sensitivity: 'accent'}) ||
         (a.email || '').localeCompare((b.email || ''), 'fi', {sensitivity: 'accent'});
}

function sortPhrases(a, b) {
  return (a.hashtag || '').localeCompare(b.hashtag || '', 'fi');
}

export default {
  state,
  getters,
  actions,
  mutations,
};
